/* =====================================================================
   LUMIQO — lq-buy-box.js (blocks lq-shipping-timeline, lq-stock-status, lq-pay-later, lq-discount-code)
   1  Variant refresh .... [data-lq-variant-refresh="<block id>"] is swapped for the same element from the
                           section HTML that Horizon's variant:update event carries (stock line, pay later).
   2  Shipping timeline .. [data-lq-ship] countdown to the dispatch cut-off + estimated dates, in the store
                           time zone (data-tz "+0400"), skipping data-weekend days.
   3  Copy code .......... [data-lq-coupon-copy] copies [data-lq-coupon-code]; status in [data-lq-coupon-status].
   Delegated listeners and one timer for the whole page, so editor re-renders and variant morphs need no
   re-initialisation. Loaded by each block; the module runs once.
   ===================================================================== */
import { ThemeEvents } from '@theme/events';

const strings = () => window.Lumiqo?.t || ((/** @type {string} */ key) => key);

/* ---------------------------------------------------------------
   1  VARIANT REFRESH
   --------------------------------------------------------------- */
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const html = /** @type {any} */ (event).detail?.data?.html;
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  if (!(html instanceof Document) || !section) return;
  for (const el of section.querySelectorAll('[data-lq-variant-refresh]')) {
    const id = el.getAttribute('data-lq-variant-refresh');
    const next = id ? html.querySelector(`[data-lq-variant-refresh="${CSS.escape(id)}"]`) : null;
    if (next) el.replaceWith(document.importNode(next, true));
  }
});

/* ---------------------------------------------------------------
   2  SHIPPING TIMELINE
   --------------------------------------------------------------- */
/** @type {Record<string, number[]>} */
const WEEKENDS = { sat_sun: [6, 0], fri_sat: [5, 6], fri: [5], sun: [0], none: [] };
const DAY_MS = 86400000;

/** "+0400" → minutes east of UTC */
const parseOffset = (/** @type {string} */ tz) => {
  const m = /^([+-])(\d{2})(\d{2})$/.exec(tz || '');
  return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
};

/**
 * Store wall-clock time held in a Date's UTC fields, so getUTC* read the store's local values.
 * @param {number} offsetMin
 */
const storeNow = (offsetMin) => new Date(Date.now() + offsetMin * 60000);

/**
 * @param {Date} date
 * @param {number[]} off
 */
const isWorkday = (date, off) => !off.includes(date.getUTCDay());

/**
 * Next workday on or after `date`.
 * @param {Date} date
 * @param {number[]} off
 */
const nextWorkday = (date, off) => {
  let d = new Date(date);
  for (let i = 0; i < 7 && !isWorkday(d, off); i++) d = new Date(d.getTime() + DAY_MS);
  return d;
};

/**
 * `date` plus `days` workdays.
 * @param {Date} date
 * @param {number} days
 * @param {number[]} off
 */
const addWorkdays = (date, days, off) => {
  let d = new Date(date);
  for (let left = days; left > 0; ) {
    d = new Date(d.getTime() + DAY_MS);
    if (isWorkday(d, off)) left--;
  }
  return d;
};

const lang = () => document.documentElement.lang || undefined;

/** @param {Date} a @param {Date} b */
const formatRange = (a, b) => {
  const fmt = new Intl.DateTimeFormat(lang(), { month: 'short', day: 'numeric', timeZone: 'UTC' });
  if (a.getTime() === b.getTime()) return fmt.format(a);
  return typeof fmt.formatRange === 'function' ? fmt.formatRange(a, b) : `${fmt.format(a)} – ${fmt.format(b)}`;
};

/** @param {number} minutes */
const formatLeft = (minutes) => {
  const t = strings();
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? t('timeHoursMinutes', { hours: h, minutes: m }) : t('timeMinutes', { minutes: Math.max(m, 1) });
};

/** @param {string} text */
const escapeHtml = (text) => {
  const span = document.createElement('span');
  span.textContent = text;
  return span.innerHTML;
};

/** @param {HTMLElement} el */
function renderShip(el) {
  const off = WEEKENDS[el.dataset.weekend || ''] ?? WEEKENDS.sat_sun;
  const cutoff = Number(el.dataset.cutoff) || 0;
  const min = Number(el.dataset.deliverMin) || 1;
  const max = Math.max(Number(el.dataset.deliverMax) || min, min);
  const now = storeNow(parseOffset(el.dataset.tz || ''));
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const minutesNow = now.getUTCHours() * 60 + now.getUTCMinutes();

  const shipsToday = isWorkday(today, off) && minutesNow < cutoff * 60;
  const ship = shipsToday ? today : nextWorkday(new Date(today.getTime() + DAY_MS), off);

  const lead = el.querySelector('[data-lq-ship-lead]');
  if (lead instanceof HTMLElement) {
    const template = shipsToday ? el.dataset.textToday : el.dataset.textLater;
    const value = shipsToday
      ? formatLeft(cutoff * 60 - minutesNow)
      : new Intl.DateTimeFormat(lang(), { weekday: 'long', timeZone: 'UTC' }).format(ship);
    const placeholder = shipsToday ? '[time]' : '[day]';
    // dataset returns the merchant's plain text, so escape it before adding the <strong> value
    const html = escapeHtml(template || '').split(escapeHtml(placeholder)).join(`<strong>${escapeHtml(value)}</strong>`);
    if (lead.innerHTML !== html) lead.innerHTML = html;
    lead.hidden = !template;
  }

  const set = (/** @type {string} */ key, /** @type {string} */ text) => {
    const target = el.querySelector(`[data-lq-ship-date="${key}"]`);
    if (target && target.textContent !== text) target.textContent = text;
  };
  set('ship', shipsToday ? strings()('today') : formatRange(ship, ship));
  set('deliver', formatRange(addWorkdays(ship, min, off), addWorkdays(ship, max, off)));
  el.dataset.lqShipReady = '';
}

const renderAllShips = () => {
  for (const el of document.querySelectorAll('[data-lq-ship]')) if (el instanceof HTMLElement) renderShip(el);
};

if (!(/** @type {any} */ (window).__lqBuyBoxTimer)) {
  let tick = 0;
  // every second: render newly added timelines; every 30 s: refresh all of them (countdown is per minute)
  /** @type {any} */ (window).__lqBuyBoxTimer = window.setInterval(() => {
    if (document.hidden) return;
    tick = (tick + 1) % 30;
    if (tick === 0) renderAllShips();
    else for (const el of document.querySelectorAll('[data-lq-ship]:not([data-lq-ship-ready])')) if (el instanceof HTMLElement) renderShip(el);
  }, 1000);
  renderAllShips();
}

/* ---------------------------------------------------------------
   3  COPY CODE
   --------------------------------------------------------------- */
/** @type {WeakMap<Element, number>} */
const resetTimers = new WeakMap();

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-lq-coupon-copy]');
  const box = button?.closest('[data-lq-coupon]');
  if (!(button instanceof HTMLButtonElement) || !box) return;

  const code = box.querySelector('[data-lq-coupon-code]')?.textContent?.trim() || '';
  const status = box.querySelector('[data-lq-coupon-status]');
  const t = strings();
  let copied = false;
  try {
    await navigator.clipboard.writeText(code);
    copied = true;
  } catch {
    // clipboard blocked (permissions / insecure context): select the code so the shopper can copy it
    const codeEl = box.querySelector('[data-lq-coupon-code]');
    const selection = window.getSelection();
    if (codeEl && selection) {
      const range = document.createRange();
      range.selectNodeContents(codeEl);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  const label = button.dataset.label || button.textContent || '';
  button.dataset.label = label;
  button.textContent = copied ? t('copied') : label;
  if (status) status.textContent = copied ? t('codeCopied', { code }) : '';

  window.clearTimeout(resetTimers.get(button));
  resetTimers.set(
    button,
    window.setTimeout(() => {
      button.textContent = label;
    }, 2000)
  );
});
